import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, Truck, Clock, CheckCircle, AlertTriangle, RefreshCw,
  ChevronRight, ExternalLink, Play, RotateCcw, MapPin, User, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status Badge Component
const FulfillmentStatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pending', icon: Clock },
    shipped: { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Shipped', icon: Truck },
    delivered: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Delivered', icon: CheckCircle },
    failed: { color: 'bg-red-100 text-red-800 border-red-300', label: 'Failed', icon: AlertTriangle },
  };
  
  const { color, label, icon: Icon } = config[status] || config.pending;
  
  return (
    <Badge className={`${color} border gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Courier Badge
const CourierBadge = ({ courier }) => {
  const colors = {
    godap: 'bg-blue-500 text-white',
    gig_logistics: 'bg-orange-500 text-white',
  };
  
  const names = {
    godap: 'Go-Dap',
    gig_logistics: 'GIG Logistics',
  };
  
  return (
    <Badge className={colors[courier] || 'bg-gray-500 text-white'}>
      {names[courier] || courier || 'Unknown'}
    </Badge>
  );
};

// Fulfillment Widget for Admin Dashboard
export const FulfillmentWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fulfillment/stats`);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch fulfillment stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse flex items-center gap-3">
            <div className="w-10 h-10 bg-muted rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-24 mb-2"></div>
              <div className="h-3 bg-muted rounded w-32"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  const hasPending = stats.pending_fulfillment > 0;

  return (
    <Card className={hasPending ? 'border-yellow-200 bg-yellow-50/50' : 'border-green-200 bg-green-50/50'}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              hasPending ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600'
            }`}>
              {hasPending ? <Clock className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            </div>
            <div>
              <p className="font-medium">
                {hasPending ? `${stats.pending_fulfillment} Awaiting Shipment` : 'All Orders Fulfilled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {stats.shipped} shipped · {stats.fulfillment_rate} rate
              </p>
            </div>
          </div>
          <Link to="/admin/fulfillment">
            <Button variant="ghost" size="sm" className="gap-1">
              View <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

// Full Fulfillment Dashboard Page
const FulfillmentPage = () => {
  const [stats, setStats] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [shippedOrders, setShippedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingOrders, setProcessingOrders] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, shippedRes] = await Promise.all([
        axios.get(`${API}/fulfillment/stats`),
        axios.get(`${API}/fulfillment/pending?limit=50`),
        axios.get(`${API}/fulfillment/shipped?limit=50`)
      ]);
      
      setStats(statsRes.data.stats);
      setPendingOrders(pendingRes.data.orders || []);
      setShippedOrders(shippedRes.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch fulfillment data:', error);
      toast.error('Failed to load fulfillment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processOrder = async (orderId) => {
    setProcessingOrders(prev => new Set([...prev, orderId]));
    try {
      const response = await axios.post(`${API}/fulfillment/process`, {
        order_id: orderId
      });
      
      if (response.data.success) {
        toast.success(`Order ${orderId.slice(0, 8)}... shipped successfully!`);
        fetchData(); // Refresh data
      } else {
        toast.error(response.data.error || 'Failed to process order');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process order');
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const retryFulfillment = async (orderId) => {
    setProcessingOrders(prev => new Set([...prev, orderId]));
    try {
      const response = await axios.post(`${API}/fulfillment/retry/${orderId}`);
      
      if (response.data.success) {
        toast.success('Fulfillment retry successful!');
        fetchData();
      } else {
        toast.error(response.data.error || 'Retry failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry fulfillment');
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const batchProcess = async () => {
    if (pendingOrders.length === 0) return;
    
    const orderIds = pendingOrders.slice(0, 10).map(o => o.id);
    
    try {
      const response = await axios.post(`${API}/fulfillment/batch`, orderIds);
      
      if (response.data.success) {
        toast.success(`Processed ${response.data.successful}/${response.data.processed} orders`);
        fetchData();
      }
    } catch (error) {
      toast.error('Batch processing failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon">
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  Order Fulfillment
                </h1>
                <p className="text-sm text-muted-foreground">
                  Manage shipping and track orders
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {pendingOrders.length > 0 && (
                <Button onClick={batchProcess} variant="default" className="gap-2">
                  <Play className="w-4 h-4" />
                  Ship All ({Math.min(pendingOrders.length, 10)})
                </Button>
              )}
              <Button onClick={fetchData} variant="outline" className="gap-2">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className={stats.pending_fulfillment > 0 ? 'border-yellow-300' : ''}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-yellow-600">{stats.pending_fulfillment}</p>
                <p className="text-sm text-muted-foreground">Pending Shipment</p>
              </CardContent>
            </Card>
            <Card className="border-blue-300">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.shipped}</p>
                <p className="text-sm text-muted-foreground">Shipped</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{stats.total_paid_orders}</p>
                <p className="text-sm text-muted-foreground">Total Paid Orders</p>
              </CardContent>
            </Card>
            <Card className="border-green-300">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{stats.fulfillment_rate}</p>
                <p className="text-sm text-muted-foreground">Fulfillment Rate</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Courier Stats */}
        {stats?.by_courier && Object.keys(stats.by_courier).length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Shipments by Courier</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 flex-wrap">
                {Object.entries(stats.by_courier).map(([courier, count]) => (
                  <div key={courier} className="flex items-center gap-2">
                    <CourierBadge courier={courier} />
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'pending'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Clock className="w-4 h-4" />
            Pending ({pendingOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('shipped')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'shipped'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Truck className="w-4 h-4" />
            Shipped ({shippedOrders.length})
          </button>
        </div>

        {/* Orders List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center text-muted-foreground">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-muted-foreground/50" />
                Loading orders...
              </div>
            ) : activeTab === 'pending' ? (
              pendingOrders.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="font-medium text-green-700">All Caught Up!</p>
                  <p className="text-sm text-muted-foreground">
                    No orders awaiting shipment
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {pendingOrders.map((order) => (
                    <div key={order.id} className="p-4 hover:bg-muted/50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono text-sm font-medium">
                              #{order.id?.slice(0, 8)}
                            </span>
                            <FulfillmentStatusBadge status="pending" />
                            <span className="text-sm text-muted-foreground">
                              {formatDate(order.created_at)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span>{order.customer_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <span className="truncate">{order.customer_email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="truncate">
                                {order.shipping_details?.city || order.shipping_address?.split(',')[0] || 'N/A'}
                              </span>
                            </div>
                          </div>

                          <div className="mt-2 flex items-center gap-4 text-sm">
                            <span className="font-medium">${order.total?.toFixed(2)}</span>
                            <span className="text-muted-foreground">
                              {order.items?.length || 0} item(s)
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => processOrder(order.id)}
                            disabled={processingOrders.has(order.id)}
                            size="sm"
                            className="gap-1"
                          >
                            {processingOrders.has(order.id) ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Truck className="w-4 h-4" />
                            )}
                            Ship Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              shippedOrders.length === 0 ? (
                <div className="py-12 text-center">
                  <Package className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="font-medium">No Shipped Orders</p>
                  <p className="text-sm text-muted-foreground">
                    Orders will appear here after shipping
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {shippedOrders.map((order) => (
                    <div key={order.id} className="p-4 hover:bg-muted/50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono text-sm font-medium">
                              #{order.id?.slice(0, 8)}
                            </span>
                            <FulfillmentStatusBadge status="shipped" />
                            {order.courier && <CourierBadge courier={order.courier} />}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-muted-foreground">Customer: </span>
                              <span>{order.customer_name}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Tracking: </span>
                              <span className="font-mono">{order.tracking_number || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Shipped: </span>
                              <span>{formatDate(order.shipped_at)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Total: </span>
                              <span className="font-medium">${order.total?.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Link to={`/track/${order.id}`} target="_blank">
                            <Button variant="outline" size="sm" className="gap-1">
                              <ExternalLink className="w-4 h-4" />
                              Track
                            </Button>
                          </Link>
                          <Button
                            onClick={() => retryFulfillment(order.id)}
                            disabled={processingOrders.has(order.id)}
                            variant="ghost"
                            size="sm"
                            className="gap-1"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FulfillmentPage;
